import React from "react";
import { Box, Tooltip, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";

export const DAILY_TASKS_HINTS = {
  page: "Turnaround and open work by ops stage. Projects, Mockup, File / CDR, PR, PO, Raw inward, Print outward, and Print inward are live.",
  date: "Date range keeps projects approved (created) in that window. Activity only counts events on those projects.",
  dateMockup:
    "Date range = projects approved (created) in the window. Activity logos / mockups / rework / reject / approval only count on those projects, timed by when the event happened.",
  dateProject: "Projects approved (created) in these dates (stock and custom).",
  openWorkProject:
    "Order-level open work: no request, awaiting finance, or variants incomplete.",
  cycleProject:
    "Days from project approved until finance approved and every product is variants-covered.",
  slaShareProject:
    "Share of cycle overrun from finance decision and variants covering stages.",
  drawerProjectElapsed: "Hours on the current open-work SLA clock.",
  drawerProjectSla:
    "Target hours for the active open-work state (request/finance or variants).",
  drawerProjectVariants: "Active variant SKU qty vs ordered product qty.",
  drawerProjectFinance:
    "Project request finance approval date (not PR finance).",
  drawerProjectInvoice: "First active customer invoice date on the order.",
  dateFile:
    "Date range = projects approved in the window. Activity file / send counts only on those projects, timed by when the event happened.",
  datePr:
    "Date range = projects approved in the window. Activity PR counts only on those projects, timed by when the event happened.",
  datePo:
    "Date range = projects approved in the window. Activity PO / email counts only on those projects, timed by when the event happened.",
  dateRaw:
    "Default: project approved. Or filter by Expected inward / Print inward timeline — a product matches if any of its dates fall in the range. Activity uses the same cohort; event times still filter the tile counts.",
  datePrint:
    "Date range = projects approved in the window. Activity outward counts only on those projects, timed by when the event happened.",
  datePrintIn:
    "Default: project approved. Or filter by Expected inward / Print inward timeline — a product matches if any of its dates fall in the range. Activity uses the same cohort; event times still filter the tile counts.",
  dateFieldInward:
    "Which dates the range applies to for the product cohort (queue, TAT, and activity). Project approved is the default. Expected inward / Print inward timeline match if any of those dates is in range.",
  openOrders:
    "Yes also includes unfinished work already past its SLA, timed as if it finished now (same rule as Ops TAT).",
  includeRunning:
    "Yes also includes unfinished work already past its SLA, timed as if it finished now (same rule as Ops TAT).",
  excludedFromSourcing:
    "Yes hides projects marked excluded from sourcing, same as Ops TAT.",
  cycle:
    "Days from project approved until every customised product on the order is approved. Average of those finished cycles, plus open ones when Include running is Yes.",
  cycleFile:
    "Days from mockup approval until every customised product on the order is sent to the printer. Average of those finished cycles, plus open ones when Include running is Yes.",
  cyclePr:
    "Days from variants covering until every product on the order has enough clean PR quantity. Average of those finished cycles, plus open ones when Include running is Yes.",
  cyclePo:
    "Days from project approved until every purchase order on the order has been emailed to the vendor. Average of those finished cycles, plus open ones when Include running is Yes.",
  cycleRaw:
    "Days from the first PO emailed to the vendor until every product on the order is QC-cleared. Average of those finished cycles, plus open ones when Include running is Yes.",
  cyclePrint:
    "Days from the earliest QC clear until every QC-cleared custom product is fully outward (MAX sent covers ordered). Average of those finished cycles, plus open ones when Include running is Yes.",
  cyclePrintIn:
    "Days from the earliest full outward until every fully outward custom product is print QC-cleared. Average of those finished cycles, plus open ones when Include running is Yes.",
  slaShare:
    "Share of fully approved orders that finished within the cycle SLA.",
  slaShareFile:
    "Share of fully sent-to-printer orders that finished within the File / CDR cycle SLA.",
  slaSharePr:
    "Share of orders that reached clean covering PR qty within the PR creation SLA.",
  slaSharePo:
    "Share of orders that emailed every issued PO to the vendor within the PO creation SLA, timed from project approved.",
  slaShareRaw:
    "Share of orders that QC-cleared every issued-PO product within the order type inward + QC SLA.",
  slaSharePrint:
    "Share of orders that reached full outward on every QC-cleared custom product within the print outward SLA.",
  slaSharePrintIn:
    "Share of orders that print QC-cleared every fully outward custom product within the print inward + QC SLA.",
  topDrag: "The step that added the most extra days past its SLA.",
  customMix: "Share of orders in this range that need customisation.",
  created_to_logo:
    "Logo TAT is days from project approved to the first logo on that product or order.",
  created_to_upload:
    "First mockup TAT is days from project approved to the first mockup file.",
  created_to_last_upload:
    "Last mockup TAT is days from project approved to the latest mockup file. Shown for context — it is not in the cycle SLA.",
  upload_to_approval:
    "Approval TAT is days from the last mockup file to client approval.",
  approved_to_first_file:
    "First file TAT is days from mockup approval to the first printing file.",
  approved_to_last_file:
    "Last file TAT is days from mockup approval to the latest printing file. Shown for context — it is not in the cycle SLA.",
  first_file_to_printer:
    "First file → sent TAT is days from the first printing file to marking sent to printer. Shown for context — it is not in the cycle SLA.",
  file_to_printer:
    "Last file → sent TAT is days from the last printing file to marking the product sent to printer.",
  approved_to_sent:
    "Mockup approval → sent TAT is days from mockup approval to sent to printer. Shown for context — it is not in the cycle SLA.",
  handoff_to_first_pr:
    "First PR TAT is days from variants covering to the first purchase request. Shown for context — it is not in the cycle SLA. Products without covering variants are excluded.",
  handoff_to_covering_pr:
    "Covering PR TAT is days from variants covering until clean PR quantity covers ordered qty. Approvals are not required. Products without covering variants are excluded.",
  pr_to_sourcing:
    "Sourcing approval TAT is days from each purchase request being created to sourcing approval. Shown for context — it is not in the cycle SLA.",
  pr_to_finance:
    "Finance approval TAT is days from each purchase request being created to finance approval. Shown for context — it is not in the cycle SLA.",
  finance_to_po:
    "Finance → PO TAT is days from PR finance approval to the purchase order being created. Shown for context — it is not in the cycle SLA.",
  po_to_vendor:
    "Vendor email TAT is days from each issued PO created to the vendor email on that PO (email_sent_date). Same clock as Ops TAT PO → sent to vendor.",
  po_to_raw_in:
    "Inward TAT is days from PO sent (email_sent_date) to MAX(inward end time) when received qty covers ordered qty. Stock-issue inwards with no PO stay out of measured samples.",
  raw_in_to_raw_qc:
    "QC TAT is days from the first inward to MAX(QC end time) when net received covers ordered qty and QC is closed. Same clock as Ops TAT. Hold time is included.",
  logos_uploaded: "How many logos were uploaded in these dates.",
  mockups_uploaded: "How many mockup files were uploaded in these dates.",
  products_touched:
    "How many products received at least one mockup file in these dates.",
  uploads_per_product:
    "Mockups uploaded ÷ products with upload (mean files per product). Click to see each product’s file count.",
  changes_requested: "How many mockups had changes requested in these dates.",
  mockups_rejected: "How many mockups were rejected in these dates.",
  mockups_approved: "How many mockup files were approved in these dates.",
  mockups_deleted:
    "How many mockup files were soft-deleted (active = 0) with modified_date in these dates.",
  files_uploaded: "How many printing files were uploaded in these dates.",
  file_products_touched:
    "How many products received at least one printing file in these dates.",
  files_sent_to_printer:
    "How many products were marked sent to printer in these dates.",
  prs_created: "How many purchase requests were created in these dates.",
  pr_products_touched:
    "How many products received at least one purchase request in these dates.",
  covering_prs:
    "How many products first reached clean covering PR quantity in these dates.",
  pos_created: "How many purchase orders were issued in these dates.",
  po_products_touched:
    "How many products received at least one purchase order in these dates.",
  all_pos:
    "How many products first got a purchase order for every approved PR in these dates.",
  emails_sent:
    "How many purchase orders were emailed to the vendor in these dates.",
  pos_cancelled:
    "How many purchase orders were cancelled (status CANCELLED) with modified_date in these dates.",
  raw_grns: "How many inwards were logged in these dates.",
  raw_full_inward:
    "How many products first reached full inward quantity in these dates.",
  raw_qc_closed: "How many products had QC closed in these dates.",
  raw_qty_rejected: "How much quantity was rejected in these dates.",
  print_sends:
    "How many print outwards with quantity greater than zero were logged in these dates.",
  print_full:
    "How many products first reached full outward quantity in these dates.",
  print_qty: "How much incremental quantity went outward in these dates.",
  print_inwards:
    "How many print inwards with quantity greater than zero were logged in these dates.",
  print_in_full:
    "How many products first reached full print inward quantity in these dates.",
  print_in_qc_closed: "How many products had print QC closed in these dates.",
  print_in_qty_rejected:
    "How much quantity was rejected on print QC in these dates.",
  request_to_project:
    "Request → project TAT is days from projects.request.created_date to project approved (projects.created_date). Request is usually raised first. Shown for context — it is not in the cycle SLA.",
  request_to_finance:
    "Finance TAT is days from projects.request.created_date to finance_approval_date or rejection_date_by_finance. This stage is in the cycle SLA.",
  created_to_variants:
    "Variants TAT is days from project approved until every product has active variant qty covering ordered qty. This stage is in the cycle SLA.",
  created_to_invoice:
    "Invoice TAT is days from project approved to the first active customer invoice. Shown for context — it is not in the cycle SLA.",
  project_requests: "How many project requests were raised in these dates.",
  project_finance_decisions:
    "How many project requests were finance-approved or rejected in these dates.",
  project_variants_covered:
    "How many products first reached variants covering ordered qty in these dates.",
  project_invoices:
    "How many orders received their first active customer invoice in these dates.",
  openWork:
    "Products still waiting on mockup work. Each card is one state. Click Due today or Past SLA on a card to see that slice. Completing a task stays in Commander.",
  openWorkFile:
    "Products still waiting on a printing file or send-to-printer. Each card is one state. Click Due today or Past SLA on a card to see that slice. Completing a task stays in Commander.",
  openWorkPr:
    "Products still waiting for a PR or full approved qty. Partial = has a created PR but not full covering; Approval pending = waiting sourcing/finance (Swageazy only for inventory PRs over ₹20k). Same product can be on both cards. All is unique products.",
  openWorkPo:
    "Products with an approved PR that still need a purchase order or a vendor email. Each card is one state. Click Due today or Past SLA on a card to see that slice. Completing a task stays in Commander.",
  openWorkRaw:
    "Products still waiting on inward or QC. Partial inward = received but not full (rejects show on the row). Awaiting QC = QC not cleared — status shows Awaiting QC, QC in progress, or QC on hold. Rejected qty is in Status and Qty.",
  openWorkPrint:
    "Custom products with raw QC cleared that still need a full print outward. Awaiting outward = nothing outward yet (stubs ignored). Partial outward = some qty outward. Completion is MAX(sent) ≥ ordered.",
  openWorkPrintIn:
    "Custom products with full print outward that still need print inward or QC. Partial = received but not full. Awaiting QC includes not started, in progress, and on hold. Completion is net MAX(received) − MAX(rejected) ≥ ordered.",
  drawerPoElapsed:
    "Time this product has been waiting on the current PO step. No PO or partial: from the oldest approved PR that still needs a PO. Awaiting email: from the oldest issued PO that still needs a vendor email.",
  drawerPoSla:
    "Allowed time for this order type (usually 1 day). The PO cycle is project approved until every issued PO is emailed to the vendor.",
  drawerPoIssued:
    "How many approved purchase requests on this product already have an issued purchase order.",
  drawerPoEmailed:
    "How many issued purchase orders on this product have been emailed to the vendor.",
  drawerPoFirst: "When the first purchase order was issued on this product.",
  drawerPoAllIssued:
    "When every approved PR on this product first had a purchase order. Empty until then.",
  drawerPoAllEmailed:
    "When every issued PO on this product was emailed to the vendor. Empty until then. This is when the PO cycle finishes.",
  drawerPoTat:
    "Hours on the current PO wait clock. Stops when every issued PO is emailed; keeps running until then. Same start as Elapsed.",
  drawerTotalTat:
    "Hours from project approved until now. This is project age, not the PO cycle.",
  drawerRawElapsed:
    "Time this product has been waiting on the current inward/QC step. Awaiting inward uses PO sent (email to vendor). Partial inward uses first inward. Awaiting QC / QC in progress / on hold uses full inward or QC start.",
  drawerRawSla:
    "Queue colour uses the order type inward SLA while waiting on inward, and the QC SLA once QC should have started. TAT cards above use PO sent → full inward, and first inward → QC cleared.",
  drawerRawReceived:
    "MAX received quantity on this product’s inward versus ordered quantity. Rejected is MAX rejected on active QC — never summed running totals. Net must reach ordered before the product leaves this queue.",
  drawerPrintElapsed:
    "Time this product has been waiting on print outward. Awaiting outward uses the QC clear time. Partial outward uses the first real outward (sent qty > 0).",
  drawerPrintSla:
    "Allowed time from order_type.print_outward_sla_days (fallback 1 day). Completing the queue requires MAX(sent) ≥ ordered — 0-qty stubs with end_time set do not count as started or complete.",
  drawerPrintSent:
    "MAX total_quantity_sent across print outward rows for this product versus ordered quantity. Never sum running totals across joins.",
  drawerPrintInElapsed:
    "Time this product has been waiting on the current print inward/QC step. Awaiting inward uses full outward. Partial uses first print inward. Awaiting QC / in progress / on hold uses full inward or QC start.",
  drawerPrintInSla:
    "Queue colour uses the order type print inward SLA while waiting on inward, and the print QC SLA once QC should have started. TAT cards use full outward → full inward, then first inward → QC cleared.",
  drawerPrintInReceived:
    "MAX received quantity on this product’s print inward versus ordered quantity. Rejected is MAX rejected on active print QC — never summed running totals. Net must reach ordered before the product leaves this queue.",
} as const;

export const DailyTasksHint: React.FC<{ text: string }> = ({ text }) => {
  const theme = useTheme();

  return (
    <Tooltip
      title={text}
      arrow
      placement="top"
      slotProps={{
        popper: {
          sx: {
            zIndex: (muiTheme) => muiTheme.zIndex.drawer + 5000,
          },
        },
      }}
    >
      <Box
        component="span"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "help",
          color: theme.palette.customColors.greyTextColor,
          lineHeight: 0,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  );
};

export const DailyTasksSectionTitle: React.FC<{
  title: string;
  hint?: string;
  subtitle?: string;
}> = ({ title, hint, subtitle }) => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: subtitle ? 2 : 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <CustomTypography variant="heading3">{title}</CustomTypography>
        {hint ? <DailyTasksHint text={hint} /> : null}
      </Box>
      {subtitle ? (
        <CustomTypography
          variant="body2"
          color={theme.palette.customColors.greyTextColor}
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </CustomTypography>
      ) : null}
    </Box>
  );
};
